import React, { createContext, FC, useContext, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { assignVendorProcedure, list, removeVendorProcedure } from '../../../../../../graph/vendors';
import Vendor from '../../../../../../types/Vendor';

interface VendorProcedureContextData {
  vendors: Vendor[];
  assign: (vendorId: number) => Promise<void>;
  remove: (vendorId: number) => Promise<void>;
}

const VendorProcedureContext = createContext<VendorProcedureContextData>({
  vendors: [],
  assign: async () => {},
  remove: async () => {},
});

export function useVendorProcedureContext() {
  return useContext(VendorProcedureContext);
}

interface VendorProcedureProviderProps {
  procedureId: number;
  date: string;
  children: React.ReactNode;
}

export const VendorProcedureProvider: FC<VendorProcedureProviderProps> = ({ date, procedureId, children }) => {
  const { data } = useQuery(list);
  const [doAssign] = useMutation(assignVendorProcedure);
  const [doRemove] = useMutation(removeVendorProcedure);

  // Prevent concurrent mutations from multiple context instances
  const assignInProgress = useRef<Set<number>>(new Set());
  const removeInProgress = useRef<Set<number>>(new Set());

  const assign = async (vendorId: number) => {
    // Prevent duplicate assign calls
    if (assignInProgress.current.has(vendorId)) {
      return;
    }

    assignInProgress.current.add(vendorId);
    try {
      await doAssign({
        variables: {
          procedureId,
          vendorId,
        },
      });
    } finally {
      assignInProgress.current.delete(vendorId);
    }
  };

  const remove = async (vendorId: number) => {
    // Prevent duplicate remove calls
    if (removeInProgress.current.has(vendorId)) {
      return;
    }

    removeInProgress.current.add(vendorId);
    try {
      await doRemove({
        variables: {
          procedureId,
          vendorId,
        },
      });
    } finally {
      removeInProgress.current.delete(vendorId);
    }
  };

  const context = {
    vendors: data?.vendors || [],
    assign,
    remove,
  };

  return (
    <>
      <VendorProcedureContext.Provider value={context}>{children}</VendorProcedureContext.Provider>
    </>
  );
};
